import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, errMsg, pollJob, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/brand";
import { ProjectSelect } from "@/components/app/ProjectSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SEV, IST, TYPES, Pill } from "@/pages/app/Incidents";
import { PhotoPicker } from "@/pages/app/ReportIncident";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");
const Card = ({ title, children, testid }) => <section className="rounded-2xl border bg-card p-5" data-testid={testid}><h2 className="mb-3 font-semibold">{title}</h2>{children}</section>;

const Witnesses = ({ i, reload }) => {
  const [f, setF] = useState({ name: "", contact: "", statement: "" });
  const add = async () => {
    try {
      await api.post(`/incidents/${i.id}/witnesses`, f);
      setF({ name: "", contact: "", statement: "" });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Witness statements" testid="witnesses">
      <ul className="mb-3 space-y-3">{i.witnesses.map((w) => <li key={w.id} className="rounded-xl bg-muted/50 p-3 text-sm"><p className="font-medium">{w.name}{w.contact && <span className="text-xs text-muted-foreground"> · {w.contact}</span>}</p><p className="mt-1 whitespace-pre-line">{w.statement}</p><p className="mt-1 text-xs text-muted-foreground">Recorded by {w.recorded_by} · {fmt(w.at)}</p></li>)}
        {i.witnesses.length === 0 && <li className="text-sm text-muted-foreground">No statements recorded.</li>}</ul>
      <div className="grid gap-2 sm:grid-cols-2"><Input placeholder="Witness name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="witness-name" /><Input placeholder="Contact (optional)" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} /></div>
      <Textarea className="mt-2" rows={3} placeholder="Statement in the witness's own words" value={f.statement} onChange={(e) => setF({ ...f, statement: e.target.value })} data-testid="witness-statement" />
      <Button size="sm" className="mt-2" onClick={add} disabled={f.name.length < 2 || f.statement.length < 5} data-testid="witness-add">Add statement</Button>
    </Card>
  );
};

const Actions = ({ i, members, reload, me }) => {
  const [f, setF] = useState({ title: "", kind: "corrective", assignee_id: "", due_date: "" });
  const [ev, setEv] = useState({});
  const add = async () => {
    try {
      await api.post(`/incidents/${i.id}/actions`, f);
      setF({ title: "", kind: "corrective", assignee_id: "", due_date: "" });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const done = async (a, status) => {
    try {
      await api.patch(`/incidents/${i.id}/actions/${a.id}`, { status, evidence: ev[a.id] || a.evidence || "" });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Card title="Corrective & preventive actions" testid="actions">
      <ul className="space-y-2">{i.actions.map((a) => (
        <li key={a.id} className="rounded-xl border p-3 text-sm" data-testid={`action-${a.id}`}>
          <div className="flex flex-wrap items-center gap-2">{a.status === "done" ? <CheckCircle2 className="h-4 w-4 text-health" /> : <AlertTriangle className={`h-4 w-4 ${a.due_date < today ? "text-alert" : "text-[#FFA000]"}`} />}
            <span className="flex-1 font-medium">{a.title}</span><span className="text-xs capitalize text-muted-foreground">{a.kind} · {a.assignee_name} · due {a.due_date}</span></div>
          {a.status === "done" ? <p className="mt-1 text-xs text-muted-foreground">Evidence: {a.evidence} — {a.completed_by}, {fmt(a.completed_at)}</p> : (i.can_manage || a.assignee_id === me) && (
            <div className="mt-2 flex gap-2"><Input placeholder="Evidence of completion" value={ev[a.id] || ""} onChange={(e) => setEv({ ...ev, [a.id]: e.target.value })} data-testid={`action-evidence-${a.id}`} /><Button size="sm" onClick={() => done(a, "done")} data-testid={`action-done-${a.id}`}>Mark done</Button></div>)}
        </li>))}
        {i.actions.length === 0 && <li className="text-sm text-muted-foreground">No actions yet.</li>}</ul>
      {i.can_manage && i.status !== "closed" && <div className="mt-4 grid gap-2 border-t pt-4 sm:grid-cols-2">
        <Input className="sm:col-span-2" placeholder="Action, e.g. Re-train forklift operators on load limits" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="action-title" />
        <select className="rounded-md border bg-background px-3 py-2 text-sm" value={f.assignee_id} onChange={(e) => setF({ ...f, assignee_id: e.target.value })} data-testid="action-assignee"><option value="">Assign to…</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
        <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="action-due" />
        <select className="rounded-md border bg-background px-3 py-2 text-sm" value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}><option value="corrective">Corrective</option><option value="preventive">Preventive</option></select>
        <Button onClick={add} disabled={!f.title || !f.assignee_id || !f.due_date} data-testid="action-add">Add action</Button></div>}
    </Card>
  );
};

const AiHelp = ({ i, reload }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/${i.id}/ai-questions`);
      await pollJob(data.job_id);
      reload();
    } catch (e) {
      toast.error(e.response ? errMsg(e) : e.message);
    }
    setBusy(false);
  };
  const q = i.ai_questions;
  return (
    <Card title="Investigation support" testid="ai-help">
      <p className="mb-3 text-xs text-muted-foreground">Saflr suggests questions and possible factors to check, using only the facts recorded here. It does not determine the root cause. 1 credit.</p>
      <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="ai-questions-btn"><Sparkles className="mr-1.5 h-4 w-4" />{busy ? "Preparing questions…" : q ? "Refresh suggestions" : "Suggest investigation questions"}</Button>
      {q && <div className="mt-4 space-y-3 text-sm">
        <div><p className="font-medium">Questions to ask</p><ol className="mt-1 list-decimal space-y-0.5 pl-5">{q.investigation_questions?.map((x, k) => <li key={k}>{x}</li>)}</ol></div>
        <div><p className="font-medium">Possible contributing factors (unconfirmed)</p><ul className="mt-1 space-y-1">{q.possible_contributing_factors?.map((x, k) => <li key={k}><b>{x.factor}</b> — <span className="text-muted-foreground">check: {x.evidence_to_check}</span></li>)}</ul></div>
        <div><p className="font-medium">Evidence to gather</p><ul className="mt-1 list-disc pl-5">{q.evidence_to_gather?.map((x, k) => <li key={k}>{x}</li>)}</ul></div>
        {q.riddor_considerations && <p className="rounded-xl bg-[#FFA000]/10 p-3 text-xs"><b>RIDDOR considerations:</b> {q.riddor_considerations} Verify against current HSE RIDDOR requirements.</p>}
      </div>}
    </Card>
  );
};

const Manage = ({ i, members, reload }) => {
  const [f, setF] = useState({ severity: i.severity, findings: i.findings || "", root_cause: i.root_cause || "", riddor_status: i.riddor?.status || "not_screened", riddor_note: i.riddor?.note || "", closure_note: "" });
  const save = async (extra = {}) => {
    try {
      await api.patch(`/incidents/${i.id}`, { ...f, ...extra });
      toast.success("Incident updated");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Investigation & review" testid="manage">
      <div className="space-y-3 text-sm">
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1"><Label>Severity</Label><select className="w-full rounded-md border bg-background px-3 py-2" value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })}>{Object.keys(SEV).map((s) => <option key={s}>{s}</option>)}</select></div>
          <div className="space-y-1"><Label>Investigator</Label><select className="w-full rounded-md border bg-background px-3 py-2" value={i.investigator_id || ""} onChange={(e) => save({ investigator_id: e.target.value, status: i.status === "reported" ? "under_investigation" : undefined })} data-testid="investigator-select"><option value="">Not assigned</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></div>
          <div className="space-y-1 sm:col-span-2"><Label>Project</Label><ProjectSelect value={i.project_id} onChange={(v) => save({ project_id: v })} testid="incident-manage-project" /></div>
        </div>
        <div className="space-y-1"><Label>Investigation findings</Label><Textarea rows={3} value={f.findings} onChange={(e) => setF({ ...f, findings: e.target.value })} data-testid="findings" /></div>
        <div className="space-y-1"><Label>Root cause (as established by the investigation)</Label><Textarea rows={2} value={f.root_cause} onChange={(e) => setF({ ...f, root_cause: e.target.value })} data-testid="root-cause" /></div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="space-y-1"><Label>RIDDOR screening</Label><select className="w-full rounded-md border bg-background px-3 py-2" value={f.riddor_status} onChange={(e) => setF({ ...f, riddor_status: e.target.value })} data-testid="riddor-status"><option value="not_screened">Not screened</option><option value="possibly_reportable">Possibly reportable — verify</option><option value="not_reportable">Not reportable</option><option value="reported">Reported to HSE</option></select></div>
          <div className="space-y-1"><Label>Screening note</Label><Input value={f.riddor_note} onChange={(e) => setF({ ...f, riddor_note: e.target.value })} placeholder="Basis for decision / HSE reference" /></div>
        </div>
        <p className="text-xs text-muted-foreground">Reporting decisions must be checked against current RIDDOR requirements by a competent person.</p>
        <Button size="sm" onClick={() => save()} data-testid="incident-save">Save</Button>
        {i.status !== "closed" && <div className="border-t pt-3"><Label>Management review / closure note</Label><Textarea rows={2} className="mt-1" value={f.closure_note} onChange={(e) => setF({ ...f, closure_note: e.target.value })} data-testid="closure-note" />
          <Button size="sm" variant="outline" className="mt-2" onClick={() => save({ status: "closed" })} data-testid="incident-close">Close incident</Button></div>}
      </div>
    </Card>
  );
};

export default function IncidentView() {
  const { id } = useParams();
  const [i, setI] = useState(null);
  const [members, setMembers] = useState([]);
  const [more, setMore] = useState([]);
  const { user: me } = useAuth();
  const load = () => api.get(`/incidents/${id}`).then((r) => setI(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/org/members").then((r) => setMembers(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!i) return <Loader />;
  const upload = async () => {
    try {
      await api.post(`/incidents/${id}/photos`, { photos: more });
      setMore([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mx-auto max-w-6xl space-y-6" data-testid="incident-view">
      <Link to="/app/incidents" className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />Incidents</Link>
      <div><div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs text-muted-foreground">{i.ref}</span><Pill c={SEV[i.severity]}>{i.severity}</Pill><Pill c="bg-muted" testid="incident-status">{IST[i.status]}</Pill></div>
        <h1 className="mt-2 text-3xl font-bold">{i.title}</h1><p className="text-sm text-muted-foreground">{TYPES[i.type]} · {i.location || "No location"} · {fmt(i.occurred_at)} · reported by {i.reported_by_name}{i.project_id && <> · <Link to={`/app/projects/${i.project_id}`} className="font-medium text-health hover:underline" data-testid="incident-project-link">{i.project_ref} {i.project_name}</Link></>}</p></div>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card title="What happened"><p className="whitespace-pre-line text-sm">{i.description}</p>
            {i.immediate_actions && <><p className="mt-3 text-sm font-medium">Immediate actions</p><p className="text-sm">{i.immediate_actions}</p></>}
            {i.people?.length > 0 && <><p className="mt-3 text-sm font-medium">People involved</p><ul className="text-sm">{i.people.map((p, k) => <li key={k}>{p.name}{p.role && ` (${p.role})`}{p.injury && ` — ${p.injury}`}</li>)}</ul></>}
          </Card>
          <Card title={`Photos (${i.photo_ids.length})`} testid="photos">
            <div className="flex flex-wrap gap-2">{i.photo_ids.map((p) => <a key={p} href={`${API_BASE}/incidents/${id}/photos/${p}`} target="_blank" rel="noreferrer"><img src={`${API_BASE}/incidents/${id}/photos/${p}`} alt="Incident evidence" className="h-24 w-24 rounded-xl border object-cover" /></a>)}</div>
            <div className="mt-3"><PhotoPicker photos={more} setPhotos={setMore} />{more.length > 0 && <Button size="sm" className="mt-2" onClick={upload} data-testid="upload-photos">Upload {more.length} photo(s)</Button>}</div>
          </Card>
          <Witnesses i={i} reload={load} />
          <Actions i={i} members={members} reload={load} me={me?.id} />
        </div>
        <aside className="space-y-6">
          {i.can_manage && <Manage i={i} members={members} reload={load} />}
          {i.can_manage && <AiHelp i={i} reload={load} />}
          {i.status === "closed" && <Card title="Closed"><p className="text-sm">{i.closure_note}</p><p className="mt-1 text-xs text-muted-foreground">{i.closed_by} · {fmt(i.closed_at)}</p></Card>}
          <Card title="History"><ol className="space-y-2 text-xs">{[...i.history].reverse().map((h, k) => <li key={k} className="border-l-2 border-health/40 pl-3"><p className="font-medium capitalize">{h.action.replace(/_/g, " ")}</p><p className="text-muted-foreground">{h.by} · {fmt(h.at)}</p>{h.note && <p className="text-muted-foreground">{h.note}</p>}</li>)}</ol></Card>
        </aside>
      </div>
    </div>
  );
}
